import * as React from "react";
import styled, { css } from "styled-components";

import { Text } from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries";
import {
  IOrderAuditLog,
  OrderAuditLogChangeType
} from "src/core/orders/api/orders.api";

import { AuditLogAction } from "./AuditLogAction";

interface AuditLogItemProps {
    auditLog: IOrderAuditLog;
}

export const AuditLogItem: React.ReactFCWithChildren<AuditLogItemProps> = ({ auditLog }) => {
  /**
   * Gets the log text from from the full data
   * basedon the log type
   */
  const logText = React.useMemo(() => {
    switch (auditLog.type) {
      case OrderAuditLogChangeType.AddProduct:
      case OrderAuditLogChangeType.RemoveProduct:
      case OrderAuditLogChangeType.QuantityChange:
      case OrderAuditLogChangeType.PriceChange: {
        const typedLog = auditLog as IOrderAuditLog<OrderAuditLogChangeType.AddProduct | OrderAuditLogChangeType.RemoveProduct | OrderAuditLogChangeType.QuantityChange | OrderAuditLogChangeType.PriceChange>;

        return `${typedLog.fullData.product.description} (${typedLog.fullData.product.partNumber})`;
      }
      case OrderAuditLogChangeType.ProductSubstitution:
      case OrderAuditLogChangeType.ProductReplacement: {
        const { fullData } = auditLog as IOrderAuditLog<OrderAuditLogChangeType.ProductSubstitution | OrderAuditLogChangeType.ProductReplacement>;

        return `${fullData.fromProduct.description} (${fullData.fromProduct.partNumber})`;
      }
      case OrderAuditLogChangeType.OrderExported: {
        const { fullData } = auditLog as IOrderAuditLog<OrderAuditLogChangeType.OrderExported>;

        return `${fullData.exportName} exported the order.`;
      }
      case OrderAuditLogChangeType.OrderImported: {
        const { fullData } = auditLog as IOrderAuditLog<OrderAuditLogChangeType.OrderImported>;

        return `Order imported from ${fullData.from}`;
      }

      default:
        return "";
    }
  }, [ auditLog ]);

  const formattedLogType = React.useMemo(() => {
    switch (auditLog.type) {
      case OrderAuditLogChangeType.AddProduct:
        return "Product added";
      case OrderAuditLogChangeType.RemoveProduct:
        return "Product removed";
      case OrderAuditLogChangeType.QuantityChange:
        return "Quantity changed";
      case OrderAuditLogChangeType.OrderCreated:
        return "Order created";
      case OrderAuditLogChangeType.OrderTypeChange:
        return "Converted order type";
      case OrderAuditLogChangeType.StatusChange:
        return "Changed status";
      case OrderAuditLogChangeType.PriceChange:
        return "Price change";
      case OrderAuditLogChangeType.ProductReplacement:
        return "Replaced product";
      case OrderAuditLogChangeType.ProductSubstitution:
        return "Product substitution";
      case OrderAuditLogChangeType.OrderExported:
        return "Order exported";
      case OrderAuditLogChangeType.OrderImported:
        return "Order imported";
      case OrderAuditLogChangeType.Message:
        return "Message";
      default:
        return "";
    }
  }, [ auditLog ]);

  return (
    <AuditLogItemWrapper>
      <div className="audit-log-details">
        <Text
          as="p"
          weight="bold"
        >
          {formattedLogType}
        </Text>

        <Text
          as="h3"
          weight="light"
        >
          {logText}
        </Text>
      </div>

      <div className="audit-log-actions">
        <AuditLogAction auditLog={auditLog} />
      </div>
    </AuditLogItemWrapper>
  );
};

const AuditLogItemWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    background: ${theme.colors.lightestGrey};
    padding: ${theme.spacing.padding.sm};
    border: ${theme.colors.lightGrey} solid 1px;
    margin-bottom: ${theme.spacing.margins.md};
    width: 100%;

    p {
      margin-bottom: 0;
    }

    h3 {
      font-weight: 300;
      margin-bottom: 0;
      font-size: ${theme.fonts.sizes.p}
    }

    ${breakpoint("lg", theme)} {
      flex-direction: row;
      align-items: center;

      h3 {
        font-weight: 300;
        font-size: ${theme.fonts.sizes.h3}
        margin-bottom: 0;
      }
    }
  
  `}
`;
